import React, { useState, useEffect, Fragment } from 'react';
import { Button, Form, Card, Alert, Row, Col, Table, InputGroup } from 'react-bootstrap';
import { Save, X, Plus, Minus, Truck, User, Tag, Calendar } from 'lucide-react';
import useForm from '../../hooks/useForm';
import { createOrder, getOrderById, updateOrderDetails } from '../../api/orders';
import { getAllProducts } from '../../api/products';
import { getAllRetailerByCommercial } from '../../api/retailer';
import { getDistributorPrices, getAllPartnerBytype, getPartnerByMail, getDistributorPricesCurrentUser } from '../../api/partner';
import useAuth from '../../hooks/useAuth';


const OrderForm = ({ orderId, onCancel, onSuccess }) => {

    const { isAdmin, isManager, isSalesRep, user, isDistributor } = useAuth();

    const isEditMode = Boolean(orderId);

    const hasStockAccess = isAdmin || isManager || isSalesRep;

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [clients, setClients] = useState([]);
    const [products, setProducts] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([{ productId: '', quantity: 1, customPrice: null }]);
    const [distributorPrices, setDistributorPrices] = useState({});
    const [usineData, setUsineData] = useState(null); // Pour stocker les données de l'usine


    const initialValues = {
        partnerId: '',
        partnerType: isDistributor ? 'CLIENT' : '',
        notes: '',
        deliveryAddress: '',
        referenceClient: '',
        orderDate: new Date().toISOString().split('T')[0]
    };


    useEffect(() => {
        const fetchPartnerForUser = async () => {
            if (!isDistributor || !user?.email) return;

            try {
                const partner = await getPartnerByMail(user.email);

                if (partner) {
                    setValues(prev => ({
                        ...prev,
                        partnerId: partner.id,
                    }));
                } else {
                    setError("Aucun partenaire associé trouvé pour cet utilisateur.");
                }
            } catch (err) {
                console.error("Erreur lors de la récupération du partenaire :", err);
                setError("Erreur lors du chargement des informations du partenaire.");
            }
        };

        fetchPartnerForUser();
    }, [isDistributor, user]);



    const { values, handleChange, errors, validateForm, setValues } = useForm(initialValues);

    const selectedClient = clients.find(c => String(c.id) === String(values.partnerId));
    const isDistributorOrder = isDistributor || selectedClient?.type === 'DISTRIBUTEUR';

    // --- Mode édition : charger la commande existante et pré-remplir le formulaire ---
    useEffect(() => {
        const loadOrderForEdit = async () => {
            if (!isEditMode) return;
            try {
                setLoading(true);
                const order = await getOrderById(orderId);

                const partnerType = order.type || order.partner?.partnerType || 'CLIENT';

                setValues(prev => ({
                    ...prev,
                    partnerId: order.partner?.id ?? '',
                    partnerType,
                    notes: order.notes || '',
                    deliveryAddress: order.deliveryAddress || '',
                    orderDate: order.orderDate ? order.orderDate.split('T')[0] : new Date().toISOString().split('T')[0]
                }));

                const prefilled = (order.items || []).map(it => ({
                    productId: it.product?.id ?? '',
                    quantity: it.quantity ?? 1,
                    customPrice: partnerType === 'FOURNISSEUR'
                        ? Number(it.unitPrice)
                        : null,
                    productName: it.product
                        ? (it.product.numeroProduit
                            ? `${it.product.numeroProduit} - ${it.product.name} - ${it.product.sku}`
                            : `${it.product.name} - ${it.product.sku}`)
                        : ''
                }));

                setSelectedProducts(prefilled.length > 0
                    ? prefilled
                    : [{ productId: '', quantity: 1, customPrice: null }]);
            } catch (err) {
                console.error('Erreur lors du chargement de la commande à modifier:', err);
                setError("Impossible de charger la commande à modifier.");
            } finally {
                setLoading(false);
            }
        };

        loadOrderForEdit();
    }, [isEditMode, orderId]);


    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const productsData = await getAllProducts();

                const sortedData = [...productsData].sort((a, b) => {
                    const numA = Number(a.numeroProduit);
                    const numB = Number(b.numeroProduit);
                    return numA - numB;
                });

                setProducts(sortedData);
            } catch (err) {
                setError('Failed to load data. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    // Charger l'usine Soplast automatiquement
    useEffect(() => {
        const fetchUsine = async () => {
            if (values.partnerType === 'FOURNISSEUR') {
                try {
                    // Récupérer l'usine via l'API
                    const fournisseurs = await getAllPartnerBytype('FOURNISSEUR');
                    const usine = fournisseurs.find(f => f.email === 'usine@soplast.com');
                    
                    if (usine) {
                        setUsineData(usine);
                        setValues(prev => ({
                            ...prev,
                            partnerId: usine.id
                        }));
                    }
                } catch (err) {
                    console.error("Erreur lors du chargement de l'usine:", err);
                }
            }
        };

        fetchUsine();
    }, [values.partnerType]);

    // Charger les prix spéciaux du distributeur connecté
    useEffect(() => {
        const fetchDistributorPricesForCurrentUser = async () => {
            if (isDistributor && user?.id) {
                try {
                    setLoading(true);
                    const prices = await getDistributorPricesCurrentUser(user.id);

                    const pricesMap = {};
                    prices.forEach(item => {
                        if (item.product && item.price) {
                            pricesMap[item.product.id] = item.price;
                        }
                    });

                    setDistributorPrices(pricesMap);
                } catch (err) {
                    console.error("Erreur lors du chargement des prix spéciaux:", err);
                } finally {
                    setLoading(false);
                }
            }
        };

        fetchDistributorPricesForCurrentUser();
    }, [isDistributor, user]);

    useEffect(() => {
        if (isDistributor && user?.id) {
            setValues(prev => ({
                ...prev,
                partnerId: user.id,
                partnerType: 'CLIENT'
            }));
        }
        if(isSalesRep){
            setValues(prev => ({
                ...prev,
                partnerType: 'CLIENT'
            }));
        }
    }, [isDistributor, user]);


    useEffect(() => {
        const fetchClients = async () => {
            if (!values.partnerType) return;
            if (values.partnerType === 'FOURNISSEUR') return; // Pas besoin de charger les clients pour les fournisseurs
            
            try {
                const type = values.partnerType;
                if(isSalesRep){
                  const clientsData = await getAllRetailerByCommercial();
                setClients(clientsData);

                }
                else{
                const clientsData = await getAllPartnerBytype(type);
                setClients(clientsData);

                }
            } catch (err) {
                setError('Failed to load clients.');
            }
        };
        fetchClients();
    }, [values.partnerType]);

    // Charger les prix du distributeur sélectionné
    useEffect(() => {
        const fetchDistributorPrices = async () => {
            if (!values.partnerId || values.partnerType !== 'CLIENT' || isDistributor) return;

            try {
                setLoading(true);
                const pricesData = await getDistributorPrices(values.partnerId);

                const pricesMap = {};
                pricesData.forEach(priceItem => {
                    pricesMap[priceItem.product.id] = priceItem.price;
                });

                setDistributorPrices(pricesMap);
            } catch (err) {
                console.error('Error fetching distributor prices:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchDistributorPrices();
    }, [values.partnerId, values.partnerType, isDistributor]);


    const handleProductChange = (index, field, value) => {
        // Empêcher la sélection d'un produit déjà présent dans une autre ligne
        if (field === 'productId' && value) {
            const valueNum = Number(value);
            const isDuplicate = selectedProducts.some(
                (p, i) => i !== index && Number(p.productId) === valueNum
            );
            if (isDuplicate) {
                const dupProduct = products.find(p => p.id === valueNum);
                setError(
                    `Le produit "${dupProduct ? dupProduct.name+'-'+dupProduct.sku : ''}" est déjà dans la commande. `
                    + `Modifiez plutôt sa quantité sur la ligne existante.`
                );
                const reset = [...selectedProducts];
                reset[index].productId = '';
                reset[index].productName = '';
                setSelectedProducts(reset);
                return;
            }
            // Sélection valide : on efface un éventuel message d'erreur précédent
            setError('');
        }

        const updatedProducts = [...selectedProducts];
        updatedProducts[index][field] = value;
        setSelectedProducts(updatedProducts);
    };

    const handleAddProduct = () => {
        setSelectedProducts([...selectedProducts, { productId: '', quantity: 1, customPrice: null }]);
    };

    const handleRemoveProduct = (index) => {
        if (selectedProducts.length <= 1) return;
        const updatedProducts = [...selectedProducts];
        updatedProducts.splice(index, 1);
        setSelectedProducts(updatedProducts);
    };

    const getProductPrice = (productId, index) => {
        if (values.partnerType === 'FOURNISSEUR' &&
            selectedProducts[index] &&
            selectedProducts[index].customPrice !== null) {
            return selectedProducts[index].customPrice;
        }

        const productIdNumber = Number(productId);

        if (distributorPrices[productIdNumber]) {
            return distributorPrices[productIdNumber];
        }

        const product = products.find(p => p.id === productIdNumber);
        return product ? product.basePrice : 0;
    };

    const getProductTotal = (productId, quantity, index) => {
        return getProductPrice(productId, index) * quantity;
    };

    // PCB (pièces par paquet) du produit, ou null si non défini
    const getProductPcb = (productId) => {
        const product = products.find(p => p.id === Number(productId));
        return product && product.pcb ? Number(product.pcb) : null;
    };

    const getOrderTotal = () => {
        return selectedProducts.reduce((acc, item, index) => {
            const total = getProductTotal(item.productId, item.quantity, index);
            return acc + total;
        }, 0);
    };

    const findProduct = (searchTerm) => {
        if (!searchTerm) return null;

        const trimmedTerm = searchTerm.trim().toLowerCase();

        return products.find(product => {
            if (product.name.toLowerCase() === trimmedTerm) {
                return true;
            }

            if (product.sku.toLowerCase() === trimmedTerm) {
                return true;
            }

            if (product.numeroProduit && product.numeroProduit.toString() === trimmedTerm) {
                return true;
            }

            if (product.name.toLowerCase().includes(trimmedTerm)) {
                return true;
            }

            if (product.sku.toLowerCase().includes(trimmedTerm)) {
                return true;
            }

            return false;
        });
    };

    const extractProductInfo = (selectedText) => {
        const patterns = [
            /^(\d+)\s*-\s*(.+?)\s*-\s*([^\(]+)(?:\s*\(Stock:\s*\d+\))?$/,
            /^(.+?)\s*-\s*([^\(]+)(?:\s*\(Stock:\s*\d+\))?$/,
            /^(.+)$/
        ];

        for (const pattern of patterns) {
            const match = selectedText.match(pattern);
            if (match) {
                if (match.length === 4) {
                    return {
                        numero: match[1].trim(),
                        name: match[2].trim(),
                        sku: match[3].trim()
                    };
                } else if (match.length === 3) {
                    return {
                        name: match[1].trim(),
                        sku: match[2].trim()
                    };
                } else {
                    return {
                        searchTerm: match[1].trim()
                    };
                }
            }
        }

        return { searchTerm: selectedText.trim() };
    };

    const validationSchema = {
        partnerId: {
            required: true,
            errorMessage: 'Le partenaire est requis'
        },
        partnerType: {
            required: true,
            errorMessage: 'Le type de partenaire est requis'
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess('');

        const isValid = validateForm(validationSchema);
        if (!isValid) {
            setLoading(false);
            return;
        }

        const validProducts = selectedProducts.filter(item => item.productId && Number(item.quantity) > 0);

        if (validProducts.length === 0) {
            setError('Veuillez ajouter au moins un produit à la commande');
            setLoading(false);
            return;
        }

        // Filet de sécurité : aucun produit en double dans la commande
        const productIds = validProducts.map(item => Number(item.productId));
        if (new Set(productIds).size !== productIds.length) {
            setError('Un même produit apparaît plusieurs fois. Veuillez regrouper les quantités sur une seule ligne.');
            setLoading(false);
            return;
        }

        const items = validProducts.map((item, index) => ({
            productId: Number(item.productId),
            quantity: Number(item.quantity),
            price: getProductPrice(item.productId, index)
        }));

        const orderData = {
            partnerId: Number(values.partnerId),
            partnerType: isDistributor ? 'CLIENT' : values.partnerType,
            type: isDistributor ? 'CLIENT' : values.partnerType,
            deliveryAddress: values.deliveryAddress,
            notes: values.notes,
            referenceClient: isDistributorOrder ? (values.referenceClient || null) : null,
            orderDate: values.orderDate ? `${values.orderDate}T00:00:00` : new Date().toISOString(),
            deliveryDate: new Date().toISOString(),
            items
        };

        try {
            if (isEditMode) {
                // OrderUpdateRequest : items = { productId, quantity }
                const updatePayload = {
                    partnerId: Number(values.partnerId),
                    notes: values.notes,
                    deliveryAddress: values.deliveryAddress,
                    orderDate: values.orderDate ? `${values.orderDate}T00:00:00` : null,   // ⬅ AJOUT
                    deliveryDate: new Date().toISOString(),
                    items: items.map(it => ({
                        productId: it.productId,
                        quantity: it.quantity,
                        price: it.price
                    }))
                };
                await updateOrderDetails(orderId, updatePayload);
                setSuccess('Commande mise à jour avec succès !');
            } else {
                await createOrder(orderData);
                setSuccess('Commande créée avec succès!');
            }
            setTimeout(() => onSuccess && onSuccess(), 1500);
        } catch (err) {
            setError(err.message || "Échec de l'enregistrement de la commande. Veuillez réessayer.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card className="shadow-sm">
            <Card.Header><h5>{isEditMode ? 'Modifier la commande' : 'Créer nouvelle commande'}</h5></Card.Header>
            <Card.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                {success && <Alert variant="success">{success}</Alert>}

                <Form onSubmit={handleSubmit}>
                    <Row className="mb-3">
                        <Col md={6} hidden={isDistributor || isSalesRep}>
                            <Form.Group>
                                <Form.Label>Type partenaire</Form.Label>
                                <InputGroup>
                                    <InputGroup.Text><Tag size={18} /></InputGroup.Text>
                                    <Form.Select
                                        name="partnerType"
                                        value={values.partnerType}
                                        onChange={handleChange}
                                        required
                                        disabled={isDistributor}
                                    >
                                        <option value="">Sélectionner type</option>
                                        <option value="CLIENT">Client</option>
                                        <option value="FOURNISSEUR">Fournisseur</option>
                                    </Form.Select>
                                </InputGroup>
                                {errors.partnerType && <Form.Text className="text-danger">{errors.partnerType}</Form.Text>}
                            </Form.Group>
                        </Col>
                        <Col md={6} hidden={isDistributor}>
                            <Form.Group>
                                <Form.Label>
                                    {values.partnerType === 'FOURNISSEUR' ? 'Fournisseur' : 'Client'}
                                </Form.Label>
                                <InputGroup>
                                    <InputGroup.Text><User size={18} /></InputGroup.Text>
                                    {values.partnerType === 'FOURNISSEUR' ? (
                                        // Input grisé pour l'usine
                                        <Form.Control
                                            type="text"
                                            value={usineData ? usineData.name : 'Usine Soplast'}
                                            disabled
                                            className="bg-light"
                                        />
                                    ) : (
                                        // Select pour les clients
                                        <Form.Select
                                            name="partnerId"
                                            value={values.partnerId}
                                            onChange={handleChange}
                                            required
                                            disabled={isDistributor}
                                        >
                                            <option value="">Sélectionner client</option>
                                            {clients.map(client => (
                                                <option key={client.id} value={client.id}>{client.name}</option>
                                            ))}
                                        </Form.Select>
                                    )}
                                </InputGroup>
                                {errors.partnerId && <Form.Text className="text-danger">{errors.partnerId}</Form.Text>}
                            </Form.Group>
                        </Col>
                    </Row>
                    {isDistributorOrder && (
                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Référence commande client</Form.Label>
                                    <InputGroup>
                                        <InputGroup.Text><Tag size={18} /></InputGroup.Text>
                                        <Form.Control
                                            type="text"
                                            name="referenceClient"
                                            value={values.referenceClient}
                                            onChange={handleChange}
                                            placeholder="N° de commande reçu du client"
                                        />
                                    </InputGroup>
                                    <Form.Text className="text-muted">
                                        Numéro de commande transmis par le distributeur.
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                        </Row>
                    )}

                    <Row className="mb-3">
                        <Col md={8}>
                            <Form.Group>
                                <Form.Label>Adresse de livraison</Form.Label>
                                <InputGroup>
                                    <InputGroup.Text><Truck size={18} /></InputGroup.Text>
                                    <Form.Control
                                        type="text"
                                        name="deliveryAddress"
                                        value={values.deliveryAddress}
                                        onChange={handleChange}
                                        placeholder="Entrer l'adresse de livraison"
                                    />
                                </InputGroup>
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label>Date de la commande</Form.Label>
                                <InputGroup>
                                    <InputGroup.Text><Calendar size={18} /></InputGroup.Text>
                                    <Form.Control
                                        type="date"
                                        name="orderDate"
                                        value={values.orderDate}
                                        onChange={handleChange}
                                        max={new Date().toISOString().split('T')[0]}
                                    />
                                </InputGroup>
                                <Form.Text className="text-muted">
                                    Pour ressaisir une commande passée.
                                </Form.Text>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Form.Group className="mb-3">
                        <Form.Label>Notes</Form.Label>
                        <Form.Control
                            as="textarea"
                            name="notes"
                            value={values.notes}
                            onChange={handleChange}
                            placeholder="Ajouter une note"
                        />
                    </Form.Group>

                    <Card className="mb-4">
                        <Card.Header className="bg-light d-flex justify-content-between align-items-center">
                            <h6>Produits de la commande</h6>
                            <Button type="button" variant="outline-primary" size="sm" onClick={handleAddProduct}>
                                <Plus size={16} className="me-1" /> Ajouter articles
                            </Button>
                        </Card.Header>
                        <Card.Body>
                            <div className="table-responsive">
<Table bordered>
                                <thead>
                                    <tr>
                                        <th>Produits</th>
                                        <th>Prix Unitaire</th>
                                        <th>Nb colis</th>
                                        <th>Quantité</th>
                                        <th>Total</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {selectedProducts.map((item, index) => (
                                        <tr key={index}>
                                            <td>
                                                <Form.Control
                                                    type="text"
                                                    value={item.productName || ''}
                                                    onChange={(e) => {
                                                        const searchTerm = e.target.value;

                                                        handleProductChange(index, 'productName', searchTerm);

                                                        if (searchTerm.includes(' - ') || searchTerm.match(/^\d+$/)) {
                                                            const productInfo = extractProductInfo(searchTerm);
                                                            let foundProduct = null;

                                                            if (productInfo.numero) {
                                                                foundProduct = products.find(p =>
                                                                    p.numeroProduit && p.numeroProduit.toString() === productInfo.numero
                                                                );
                                                            }

                                                            if (!foundProduct && productInfo.sku) {
                                                                foundProduct = products.find(p =>
                                                                    p.sku.toLowerCase() === productInfo.sku.toLowerCase()
                                                                );
                                                            }

                                                            if (!foundProduct && productInfo.name) {
                                                                foundProduct = products.find(p =>
                                                                    p.name.toLowerCase() === productInfo.name.toLowerCase()
                                                                );
                                                            }

                                                            if (!foundProduct && productInfo.searchTerm) {
                                                                foundProduct = findProduct(productInfo.searchTerm);
                                                            }

                                                            if (foundProduct) {
                                                                handleProductChange(index, 'productId', foundProduct.id);

                                                                if (values.partnerType === 'FOURNISSEUR') {
                                                    handleProductChange(index, 'customPrice', foundProduct.prixFournisseur != null ? foundProduct.prixFournisseur : foundProduct.basePrice);
                                                } else {
                                                                    const price = getProductPrice(foundProduct.id, index);
                                                                    handleProductChange(index, 'unitPrice', price);
                                                                }
                                                            } else {
                                                                handleProductChange(index, 'productId', '');
                                                            }
                                                        } else {
                                                            const foundProduct = findProduct(searchTerm);
                                                            if (foundProduct && searchTerm.length > 2) {
                                                                // Ne pas mettre à jour automatiquement l'ID
                                                            } else {
                                                                handleProductChange(index, 'productId', '');
                                                            }
                                                        }
                                                    }}
                                                    list={`products-list-${index}`}
                                                    placeholder="Nom, numéro ou référence du produit"
                                                    required
                                                />
                                                <datalist id={`products-list-${index}`}>
                                                    {products.map(product => (
                                                        <option
                                                            key={product.id}
                                                            value={product.numeroProduit
                                                                ? `${product.numeroProduit} - ${product.name} - ${product.sku}`
                                                                : `${product.name} - ${product.sku}`}
                                                        />
                                                    ))}
                                                </datalist>
                                            </td>
                                            <td>
                                                {values.partnerType === 'FOURNISSEUR' ? (
                                                    <InputGroup>
                                                        <Form.Control
                                                            type="number"
                                                            min="0"
                                                            onWheel={(e) => e.target.blur()}
                                                            step="0.01"
                                                            value={item.customPrice || ''}
                                                            onChange={(e) => handleProductChange(index, 'customPrice', parseFloat(e.target.value))}
                                                            placeholder="Prix"
                                                        />
                                                    </InputGroup>
                                                ) : (
                                                    <>
                                                        {getProductPrice(item.productId, index).toFixed()} F cfa{" "}
                                                        {item.productId && distributorPrices[Number(item.productId)] &&
                                                            <span className="ms-1 badge bg-success">Prix spécial</span>
                                                        }
                                                    </>
                                                )}
                                            </td>
                                            <td>
                                                {(() => {
                                                    const pcb = getProductPcb(item.productId);
                                                    if (!pcb) {
                                                        // Produit sans paquet : champ désactivé
                                                        return (
                                                            <Form.Control
                                                                type="number"
                                                                onWheel={(e) => e.target.blur()}
                                                                value=""
                                                                disabled
                                                                placeholder="—"
                                                                title="Produit sans paquet (PCB non défini)"
                                                            />
                                                        );
                                                    }
                                                    const nbPaquet = Math.floor((Number(item.quantity) || 0) / pcb);
                                                    return (
                                                        <Form.Control
                                                            type="number"
                                                            min="0"
                                                            onWheel={(e) => e.target.blur()}
                                                            value={nbPaquet > 0 ? nbPaquet : ''}
                                                            onChange={(e) => {
                                                                const raw = e.target.value;
                                                                if (raw === '') {
                                                                    // champ vidé → on vide aussi la quantité
                                                                    handleProductChange(index, 'quantity', '');
                                                                    return;
                                                                }
                                                                const nb = parseInt(raw, 10);
                                                                const safeNb = isNaN(nb) || nb < 0 ? 0 : nb;
                                                                handleProductChange(index, 'quantity', String(safeNb * pcb));
                                                            }}
                                                            title={`1 paquet = ${pcb} unités`}
                                                        />
                                                    );
                                                })()}
                                            </td>
                                            <td>
                                                <Form.Control
                                                    type="number"
                                                    min="1"
                                                    onWheel={(e) => e.target.blur()}
                                                    value={item.quantity}
                                                    onChange={(e) => handleProductChange(index, 'quantity', e.target.value)}
                                                    required
                                                />
                                            </td>
                                            <td>{getProductTotal(item.productId, item.quantity, index).toFixed()} F cfa</td>
                                            <td>
                                                <Button
                                                    type="button"
                                                    variant="outline-danger"
                                                    size="sm"
                                                    onClick={() => handleRemoveProduct(index)}
                                                    disabled={selectedProducts.length <= 1}
                                                >
                                                    <Minus size={16} />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                            </div>
                            
                            <div className="text-end mt-3">
                                <h6>Total : <strong>{getOrderTotal().toFixed()} F cfa</strong></h6>
                            </div>

                        </Card.Body>
                    </Card>

                    <div className="d-flex justify-content-end">
                        <Button type="submit" variant="primary" disabled={loading}>
                            <Save size={18} className="me-1" /> {isEditMode ? 'Mettre à jour la commande' : 'Enregistrer commande'}
                        </Button>
                        <Button type="button" variant="secondary" className="ms-2" onClick={onCancel}>
                            <X size={18} className="me-1" /> Annuler
                        </Button>
                    </div>
                </Form>
            </Card.Body>
        </Card>
    );
};

export default OrderForm;